import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SITE, TELEGRAM_URL } from "@/config";
import { trackSubscribe } from "@/lib/track";

const Hero = () => {
    return (
        <section
            data-testid="hero-section"
            className="relative overflow-hidden hero-glow text-white"
        >
            <div className="absolute inset-0 hero-grid opacity-60 pointer-events-none" />

            {/* Top nav */}
            <nav
                data-testid="site-nav"
                className="relative z-10 flex items-center justify-between px-6 md:px-12 lg:px-24 py-6"
            >
                <a
                    href="#top"
                    data-testid="nav-logo-link"
                    className="flex items-center gap-3"
                >
                    <img
                        src={SITE.logoUrl}
                        alt="MZ Trading Lab"
                        data-testid="nav-logo-img"
                        className="h-12 sm:h-14 w-auto object-contain"
                    />
                </a>
                <div className="hidden md:flex items-center gap-8 text-sm text-white/70">
                    <a
                        href="#problem"
                        data-testid="nav-link-problem"
                        className="hover:text-white transition-colors"
                    >
                        The Struggle
                    </a>
                    <a
                        href="#benefits"
                        data-testid="nav-link-solution"
                        className="hover:text-white transition-colors"
                    >
                        Inside MTL
                    </a>
                    <a
                        href="#testimonials"
                        data-testid="nav-link-benefits"
                        className="hover:text-white transition-colors"
                    >
                        Reviews
                    </a>
                    <a
                        href="#faq"
                        data-testid="nav-link-faq"
                        className="hover:text-white transition-colors"
                    >
                        FAQ
                    </a>
                </div>
                <a
                    href={TELEGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="nav-cta-button"
                    onClick={trackSubscribe}
                    className="btn-primary inline-flex items-center gap-2 bg-[#0077b6] hover:bg-[#005f92] text-white font-semibold rounded-xl px-4 md:px-5 py-2.5 text-sm transition-colors shadow-lg shadow-[#0077b6]/20"
                >
                    Join MTL
                    <ArrowRight className="h-4 w-4" />
                </a>
            </nav>

            {/* Hero content */}
            <div
                id="top"
                className="relative z-10 px-6 md:px-12 lg:px-24 pt-10 md:pt-20 pb-24 md:pb-32 grid lg:grid-cols-12 gap-12 items-center"
            >
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                    className="lg:col-span-12 lg:text-center lg:max-w-4xl lg:mx-auto"
                >
                    <div
                        data-testid="hero-trust-badge"
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs sm:text-sm text-white/80 backdrop-blur"
                    >
                        <span className="live-dot" />
                        Trusted by traders across Malaysia & Singapore
                    </div>

                    <h1
                        data-testid="hero-headline"
                        className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]"
                    >
                        Watching trading videos{" "}
                        <br className="hidden sm:block" />
                        <span className="text-white/60">but still</span>{" "}
                        <span className="text-[#3aa6e0]">losing trades?</span>
                    </h1>

                    <p
                        data-testid="hero-subheadline"
                        className="mt-6 text-base sm:text-lg text-white/70 max-w-2xl lg:mx-auto leading-relaxed"
                    >
                        You don't need to stare at charts all day or figure it
                        out alone, bro. At MZ Trading Lab you get{" "}
                        <span className="text-white">daily signals</span>, live
                        trading sessions, structured education and a community
                        that genuinely supports your growth.
                    </p>

                    <div className="mt-8 flex flex-col sm:flex-row gap-4 lg:justify-center">
                        <a
                            href={TELEGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-primary-cta"
                            onClick={trackSubscribe}
                            className="btn-primary inline-flex items-center justify-center gap-2 bg-[#0077b6] hover:bg-[#005f92] text-white font-semibold rounded-xl px-7 py-4 text-base shadow-xl shadow-[#0077b6]/30 transition-colors"
                        >
                            Join MZ Trading Lab
                            <ArrowRight className="h-5 w-5" />
                        </a>
                        <a
                            href="#benefits"
                            data-testid="hero-secondary-cta"
                            onClick={trackSubscribe}
                            className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-xl px-7 py-4 text-base border border-white/15 transition-colors"
                        >
                            See what's inside
                        </a>
                    </div>

                    {/* Mini stats */}
                    <div
                        data-testid="hero-stats"
                        className="mt-10 grid grid-cols-3 gap-6 max-w-xl lg:mx-auto"
                    >
                        <div>
                            <div className="font-display text-2xl sm:text-3xl font-bold">
                                5–15
                            </div>
                            <div className="text-xs sm:text-sm text-white/60 mt-1">
                                Premium signals daily
                            </div>
                        </div>
                        <div>
                            <div className="font-display text-2xl sm:text-3xl font-bold">
                                Live
                            </div>
                            <div className="text-xs sm:text-sm text-white/60 mt-1">
                                Trading sessions daily
                            </div>
                        </div>
                        <div>
                            <div className="font-display text-2xl sm:text-3xl font-bold">
                                MY · SG
                            </div>
                            <div className="text-xs sm:text-sm text-white/60 mt-1">
                                Local-friendly community
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Ticker */}
            <div className="relative z-10 border-y border-white/10 bg-black/40 py-4">
                <div className="marquee">
                    {[0, 1].map((k) => (
                        <div
                            key={k}
                            className="marquee-track text-sm font-mono text-white/60"
                            aria-hidden={k === 1}
                        >
                            <span>EURUSD · 1.0842 ▲ 0.12%</span>
                            <span>XAUUSD · 2,358.40 ▲ 0.42%</span>
                            <span>GBPJPY · 192.18 ▼ 0.08%</span>
                            <span>BTCUSD · 67,420 ▲ 1.10%</span>
                            <span>USDJPY · 156.21 ▲ 0.18%</span>
                            <span>NAS100 · 18,902 ▲ 0.55%</span>
                            <span>AUDUSD · 0.6612 ▼ 0.04%</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Hero;
